import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Database} from '@tnc/shared';
import debounce from 'lodash/debounce';
import {ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';

import {sortOptionT} from '@/lib/types/dataTable.types';
import {limitT, paginationObjT} from '@/lib/types/pagination.types';
import {ColumnSelectionT, CombinedColumnsT, FilterT, SelectedColumnsKeysT} from '@/lib/types/query.types';
import {FlattenT} from '@/lib/types/utility.types';

import {getSortOptions} from '@/lib/constants';
import {queryKeysE} from '@/lib/constants/queriesKeys';
import {DefaultCreateDataT, DefaultDeleteDataT, defaultCreateData, defaultDeleteData, getQueryKey} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useDataQuery} from '@/hooks';
import {useViewQuery} from '@/hooks/useViewQuery';

import {useNotifications} from '@/providers/components/notificationsProvider';

/*******************************************************************
 * Helpers Functions
 *******************************************************************/
// Helper type for creating the base context
export type BaseContextT<
  Table extends keyof Database['public']['Tables'],
  Stats extends keyof Database['public']['Views'] | undefined = undefined,
  Columns extends ColumnSelectionT<Table>[] = ['*'],
  Custom extends Record<string, unknown> = Record<string, unknown>,
> = FlattenT<BaseGenericContextT<Table, Stats, Columns> & Custom>;

/**
 * Helper Type for the createProviderConfig function
 * @template T - The table to create the provider config for
 * @template C - The columns to create the provider config for
 * @template S - The stats view to create the provider config for
 * @template F - The filters to create the provider config for
 * @param {T} table - The table to create the provider config for
 * @param {C} columns - The columns to create the provider config for
 * @param {S} statsView - The stats view to create the provider config for
 * @param {F} filters - The filters to create the provider config for
 * @returns {ProviderConfigT<T, C, S, F>}
 */
type ProviderConfigT<
  T extends keyof Database['public']['Tables'],
  C extends ColumnSelectionT<T>[] = ['*'],
  S extends keyof Database['public']['Views'] | undefined = undefined,
  F extends FilterT<T, C>[] | undefined = undefined,
> = {
  table: T;
  columns: C;
  filters?: F;
  statsView?: S;
};

type ProviderConfigPropsT<
  T extends keyof Database['public']['Tables'],
  C extends ColumnSelectionT<T>[] = ['*'],
  S extends keyof Database['public']['Views'] | undefined = undefined,
  F extends FilterT<T, C>[] | undefined = undefined,
> = {
  table: T;
  columns?: C;
  statsView?: S;
  filters?: F;
};

export function createProviderConfig<
  T extends keyof Database['public']['Tables'],
  C extends ColumnSelectionT<T>[] = ['*'],
  S extends keyof Database['public']['Views'] | undefined = undefined,
  F extends FilterT<T, C>[] | undefined = undefined,
>(config: ProviderConfigPropsT<T, C, S, F>): ProviderConfigT<T, C, S, F> {
  return {
    table: config.table,
    columns: config.columns ?? (['*'] as C),
    statsView: config.statsView,
    filters: config.filters,
  };
}

/**
 * Creates the base context
 * @param name - The name of the context
 * @returns The base context
 */
export function createAppContext<C>(name: keyof Database['public']['Tables']): React.Context<C> {
  const context = createContext<C>({} as C);
  context.displayName = `${name}Provider`;
  return context;
}

/**
 * Creates the base provider
 * @param T - The table to create the provider for
 * @param S - The stats view to create the provider for
 * @param C - The columns to create the provider for
 * @returns The base provider
 */
type CreateAppProviderPropsT<
  T extends keyof Database['public']['Tables'],
  S extends keyof Database['public']['Views'],
  C extends ColumnSelectionT<T>[] = ['*'],
> = {
  table: T;
  statsView?: S;
  columns?: C;
  enabled?: boolean;
  ContextProvider: React.ComponentType<{
    children: ReactNode;
  }>;
};

// Creates the base provider
export function createAppProvider<
  T extends keyof Database['public']['Tables'],
  S extends keyof Database['public']['Views'],
  C extends ColumnSelectionT<T>[] = ['*'],
>({table, statsView, enabled = false, ContextProvider, columns}: CreateAppProviderPropsT<T, S, C>) {
  const NamedBaseProvider = ({children}: {children: ReactNode}) => (
    <BaseProvider table={table} statsView={statsView} enabled={enabled} columns={columns}>
      <ContextProvider>{children}</ContextProvider>
    </BaseProvider>
  );

  NamedBaseProvider.displayName = table;
  return NamedBaseProvider;
}

/**
 * Creates a base hook for a app provider
 * @param context - The context to create the hook for
 * @returns The base hook
 */
export function createAppHook<
  T extends keyof Database['public']['Tables'],
  V extends keyof Database['public']['Views'] | undefined = undefined,
  C extends ColumnSelectionT<T>[] = ['*'],
  Custom extends Record<string, unknown> = Record<string, unknown>,
  AppHookContextT extends BaseGenericContextT<T, V, C> & Custom = FlattenT<BaseGenericContextT<T, V, C> & Custom>,
>(context: React.Context<AppHookContextT>) {
  const hookName = `use${context.displayName?.replace('Provider', 's') || 'Context'}`;
  const providerName = `${context.displayName || 'Context'}`;

  return () => {
    const contextValue = useContext<AppHookContextT>(context);
    if (contextValue == null) {
      throw new Error(`${hookName} must be used within a ${providerName}`);
    }
    return contextValue;
  };
}

/**
 * Creates the id column for the supplied, valid, table by combining the table's name
 * and appending _id to the end of the table name. Lastly, cast it to the ValidItemIdT.
 * Ex: table = "Retailer" -> "retailer_id"
 * @param table - The table to create the id column for
 * @returns The id column
 *
 * *Note* this not a runtime check as the entirety of the database schema is not exposed
 * to the client (e.g. we cannot get all the table names in js). We could go about it
 * another way if wanted: https://github.com/orgs/supabase/discussions/27603
 */
export function createBaseIdColumn<T extends keyof Database['public']['Tables']>(
  table: T
): keyof Database['public']['Tables'][T]['Row'] {
  const idColumn = `${table}_id`.toLocaleLowerCase();
  return idColumn as keyof Database['public']['Tables'][T]['Row'];
}

/*******************************************************************
 * BaseContext
 *******************************************************************/
type BaseGenericContextT<
  T extends keyof Database['public']['Tables'],
  V extends keyof Database['public']['Views'] | undefined = undefined,
  C extends ColumnSelectionT<T>[] = ['*'],
> = {
  // Properties
  enabled: boolean;
  data: CombinedColumnsT<T, C>[];
  stats?: V extends keyof Database['public']['Views'] ? Database['public']['Views'][V]['Row'] : undefined;
  pagination: paginationObjT<T, CombinedColumnsT<T, C>>;
  isLoading: boolean;
  isLoadingStats: boolean;
  isLoadingData: boolean;
  dataError: Error | null;
  statsError: Error | null;
  // Functions
  initialize: () => void;
  setEnabled: (enabled: boolean) => void;
  getData: (enable?: boolean) => Promise<Database['public']['Tables'][T]['Row'][]>;
  getStats: (enable?: boolean) => Promise<Database['public']['Tables'][T]['Row'] | undefined>;
  refreshData: (enable?: boolean) => Promise<void>;
  refreshStats: (enable?: boolean) => Promise<void>;
  sortData: (sortOption: sortOptionT<T>) => void;
  searchData: (searchTerm: string, debounceTime?: number) => Promise<Database['public']['Tables'][T]['Row'][]>;
  createData: (data: Database['public']['Tables'][T]['Insert']) => Promise<Database['public']['Tables'][T]['Row']>;
  updateData: (data: Database['public']['Tables'][T]['Update']) => Promise<Database['public']['Tables'][T]['Row']>;
  deleteData: (id: number) => Promise<Database['public']['Tables'][T]['Row']>;
  setFilters: (filters: FilterT<T, C>[]) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BaseContext = createContext({} as BaseGenericContextT<any, any, any>);

/*******************************************************************
 * BaseProvider
 *******************************************************************/
type baseProviderPropsT<
  T extends keyof Database['public']['Tables'],
  S extends keyof Database['public']['Views'],
  C extends ColumnSelectionT<T>[] = ['*'],
> = {
  children: ReactNode;
  table: T;
  columns?: C;
  statsView?: S;
  enabled?: boolean;
  filters?: FilterT<T, C>[];
  protected?: boolean;
};

export const BaseProvider = <
  T extends keyof Database['public']['Tables'],
  S extends keyof Database['public']['Views'],
  C extends ColumnSelectionT<T>[] = ['*'],
  SRow extends object = Database['public']['Views'][S]['Row'],
>({
  children,
  table,
  columns = ['*'] as C,
  statsView,
  enabled = false,
  filters,
}: baseProviderPropsT<T, S, C>) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const sortOptions = getSortOptions(table);
  const {toast} = useNotifications();

  // Constants
  const idColumn = createBaseIdColumn(table);
  const searchColumn = `${table.toLowerCase()}_name` as SelectedColumnsKeysT<T, C>;

  // State
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortOption, setSortOption] = useState<sortOptionT<keyof Database['public']['Tables']>>(sortOptions[0]);
  const [limit, setLimit] = useState<limitT>(5);
  const [page, setPage] = useState<number>(1);

  // Refs
  // - DEFAULT FETCHING STATE -
  // Enabled is used to determine if the react-query(ies) will run on mount and in bg,
  // disabled by default for performance reasons. Note calling setEnabled will not trigger a re-render!
  const enabledRef = useRef(enabled);
  const defaultCreateDataRef = useRef<DefaultCreateDataT | null>(null);
  const defaultDeleteDataRef = useRef<DefaultDeleteDataT | null>(null);
  const filtersRef = useRef<FilterT<T, C>[]>(filters ?? []);

  // TanStack Query hooks
  const dataQueryKey = getQueryKey.list(table, {
    columns,
    sortOption,
    page,
    limit,
    searchTerm,
    searchColumn,
    enabled,
    filters: filtersRef.current,
  });

  const {
    data: {data, total} = {data: [], total: 0},
    isLoading: isLoadingData,
    error: errorData,
    refetch: refetchData,
  } = useDataQuery<T, C>({
    table,
    columns,
    all: false,
    sortOption,
    page,
    limit,
    enabled: enabledRef.current,
    searchTerm,
    searchColumn,
    filters: filtersRef.current,
  });

  const {
    data: stats,
    isLoading: isLoadingStats,
    error: errorStats,
    refetch: refetchStats,
  } = useViewQuery<S, SRow>({
    view: statsView ?? ('dummy_view' as S),
    enabled: enabledRef.current && !!statsView,
  });

  // Pagination
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const handlePrev = () => {
    setPage(prev => Math.max(prev - 1, 1));
    window.scrollTo({top: 0, behavior: 'instant'});
  };

  const handleNext = () => {
    setPage(prev => Math.min(prev + 1, totalPages));
    window.scrollTo({top: 0, behavior: 'instant'});
  };

  const goToPage = (page: number) => {
    setPage(Math.min(Math.max(1, page), totalPages));
    window.scrollTo({top: 0, behavior: 'instant'});
  };

  const pagination: paginationObjT<T, CombinedColumnsT<T, C>> = {
    currentPage: Math.min(page, totalPages),
    totalPages,
    currentData: data,
    totalItemCount: total,
    limit,
    setLimit,
    goToPage,
    handlePrev,
    handleNext,
  };

  // Helper to clear all the entity-related queries from the tanstack cache
  const clearDataCache = useCallback(async () => {
    // Use Promise.all to invalidate queries concurrently
    await Promise.all([
      // Invalidate the stats query
      statsView &&
        queryClient.invalidateQueries({
          queryKey: getQueryKey.stats(statsView, enabled),
        }),

      // Invalidate all keys that start with the entity name, covering all possible variations
      // including sorts, pagination, and stats
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === table ||
          query.queryKey[0] === queryKeysE[`${table.toLowerCase()}_stats` as keyof typeof queryKeysE],
      }),
    ]);
  }, [enabled, queryClient, statsView, table]);

  /*******************************************************************
   * General Functions
   ********************************************************************/
  /*********************************************************************
   * Initializes the data state with data from the database and sets the enabled state to true.
   * @returns {Promise<void>}
   ********************************************************************/
  const initialize = () => {
    if (enabledRef.current) return;
    enabledRef.current = true;
    Promise.all([getData(), getStats()]);
  };

  /*********************************************************************
   * Gets the data from the database and returns the data.
   * @returns {Promise<Database['public']['Tables'][T]['Row'][]>}
   ********************************************************************/
  const getData = useCallback(
    async (autoEnable = true) => {
      try {
        if (!enabledRef.current && autoEnable) enabledRef.current = true;
        const {data: {data: _refetchedData} = {}} = await refetchData();
        const _data = _refetchedData ?? [];
        return _data;
      } catch (error) {
        console.error(`Error in get${table}:`, error);
        enabledRef.current = false;
        clearDataCache();
        return [];
      }
    },
    [clearDataCache, refetchData, table]
  );

  /*********************************************************************
   * Refreshes with data from the database.
   * @returns {Promise<void>}
   ********************************************************************/
  const refreshData = useCallback(
    async (autoEnable = true) => {
      try {
        if (!enabledRef.current && autoEnable) enabledRef.current = true;
        const {error} = await refetchData();
        if (error) throw error;
      } catch (error) {
        console.error(`Error in refresh${table}:`, error);
        enabledRef.current = false;
        clearDataCache();
      }
    },
    [clearDataCache, refetchData, table]
  );

  /*********************************************************************
   * Updates the sort option for the data.
   * @param {sortOptionT<T>} sortOption - The sort option to be applied.
   * @returns {void}
   ********************************************************************/
  const sortData = (sortOption: sortOptionT<keyof Database['public']['Tables']>) => {
    setSortOption(sortOption);
  };

  /*********************************************************************
   * Gets the stats from the database and returns them.
   * @returns {Promise<Database['public']['Views'][V]['Row']>}
   ********************************************************************/
  const getStats = useCallback(
    async (autoEnable = true): Promise<SRow | undefined> => {
      try {
        if (!enabledRef.current && autoEnable) enabledRef.current = true;
        const {data: _refetchedData} = await refetchStats();
        return _refetchedData;
      } catch (error) {
        console.error(`Error in getStats:`, error);
        enabledRef.current = false;
        clearDataCache();
      }
    },
    [clearDataCache, refetchStats]
  );

  /*********************************************************************
   * Refreshes the stats state with data from the database.
   * @returns {Promise<void>}
   ********************************************************************/
  const refreshStats = useCallback(
    async (autoEnable = true) => {
      try {
        if (!enabledRef.current && autoEnable) enabledRef.current = true;
        const {error} = await refetchStats();
        if (error) throw error;
      } catch (error) {
        console.error(`Error in refresh${table}Stats:`, error);
        enabledRef.current = false;
      }
    },
    [refetchStats, table]
  );

  /*******************************************************************
   * Search Functions
   *******************************************************************/
  /*********************************************************************
   * Searches for data based on a search term using full-text search.
   * @param {string} searchTerm - The search term to be used.
   * @param {number} [debounceTime=750] - Optional debounce time in milliseconds.
   * @returns {Promise<Database['public']['Tables'][T]['Row'][]>} - An array of data objects.
   ********************************************************************/
  const searchData = useCallback(
    async (searchTerm: string) => {
      setSearchTerm(searchTerm);
      setPage(1);

      // Reset if the search term is empty
      if (searchTerm === '') refetchData();
    },
    [refetchData]
  );

  // Create a memoized debounced version of the search function
  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        // Call the searchData function but don't worry about the return type
        searchData(searchTerm).catch(error => console.error(error));
      }, 750),
    [searchData]
  );

  // Wrapper function to maintain the type consistency and handle debounce time
  const handleDebouncedSearchData = (
    searchTerm: string,
    debounceTime?: number
  ): Promise<Database['public']['Tables'][T]['Row'][]> => {
    if (debounceTime !== undefined && debounceTime !== 750) {
      // If a custom debounce time is provided, create a new debounced function
      debounce((term: string) => {
        searchData(term).catch(error => console.error(error));
      }, debounceTime)(searchTerm);
    } else {
      // Use the memoized debounced function
      debouncedSearch(searchTerm);
    }
    // Return a resolved promise to satisfy the type requirement
    return Promise.resolve([]);
  };

  /*******************************************************************
   * CRUD Functions
   *******************************************************************/
  /*********************************************************************
   * Creates a new record in the database.
   * @param {dataInsertTypeT} data - The data object to be created.
   * @returns {Promise<Database['public']['Tables'][T]['Row']>} - The newly created data object.
   ********************************************************************/

  const {mutateAsync: createData} = useMutation({
    mutationKey: [table, 'create'],
    mutationFn: async data => {
      // Ensure defaultCreateDataRef.current is set
      defaultCreateDataRef.current = await defaultCreateData();

      // Merge data with defaultCreateDataRef.current
      const insertData: Database['public']['Tables'][T]['Insert'] = {
        ...data,
        ...defaultCreateDataRef.current,
      };
      // Create the record
      const {data: newData, error} = await SupabaseInstance.supabase
        .from(table)
        .insert(insertData as never)
        .select()
        .single<Database['public']['Tables'][T]['Row']>();

      // Reset the defaultCreateData ref
      defaultCreateDataRef.current = null;

      if (error) throw error;
      return newData;
    },
    onMutate: async (newData: Database['public']['Tables'][T]['Row']) => {
      // Cancel any queries
      await Promise.all([
        queryClient.cancelQueries({queryKey: dataQueryKey}),
        queryClient.cancelQueries({queryKey: getQueryKey.detail(table, Number(newData[idColumn]), columns, enabled)}),
        statsView ? queryClient.cancelQueries({queryKey: getQueryKey.stats(statsView, enabled)}) : null,
      ]);

      // Optimistically add the detail page
      queryClient.setQueryData([table, newData[idColumn]], newData);

      // Optimistically update the stats
      if (statsView)
        queryClient.setQueryData(
          getQueryKey.stats(statsView, enabled),
          (old: Database['public']['Views'][S]['Row']) => {
            return old
              ? {
                  ...old,
                  active: 'active' in old && old.active !== null ? old.active + 1 : null,
                  total: 'total' in old && old.total !== null ? old.total + 1 : null,
                }
              : {};
          }
        );

      // Return rollback data
      return {
        previousStats: statsView ? queryClient.getQueryData(getQueryKey.stats(statsView, enabled)) : undefined,
        previousData: queryClient.getQueryData(dataQueryKey),
      };
    },
    onError: (error, newData, context) => {
      // Rollback the optimistic updates on error
      if (context?.previousStats && statsView)
        queryClient.setQueryData(getQueryKey.stats(statsView, enabled), context.previousStats);
      if (context?.previousData) queryClient.setQueryData(dataQueryKey, context.previousData);
      queryClient.invalidateQueries({queryKey: [table, newData[idColumn]]});
      defaultDeleteDataRef.current = null;
      console.error(`Error creating ${table}:`, error);
      toast.error(`Error creating ${table}`);
    },
    onSuccess: (data: Database['public']['Tables'][T]['Row']) => {
      toast.success(`${table} created successfully. Click here to view.`, {
        onClick: () => navigate(`/${table.toLowerCase()}s/${data[idColumn]}`),
      });
      // Invalidate all cached queries as adding a new record could affect them all
      clearDataCache();
    },
  });

  /*********************************************************************
   * Updates an existing record in the database.
   * @param {dataUpdateTypeT} data - The data object to be updated.
   * @returns {Promise<Database['public']['Tables'][T]['Row']>}
   ********************************************************************/
  const {mutateAsync: updateData} = useMutation({
    mutationKey: [table, 'update'],
    // Cant use update type here??
    mutationFn: async (data: Database['public']['Tables'][T]['Row']) => {
      const {data: updatedData, error} = await SupabaseInstance.supabase
        .from(table)
        .update(data as never)
        .eq(String(idColumn), String(data[idColumn]))
        .select()
        .single<Database['public']['Tables'][T]['Row']>();

      if (error) throw error;
      return updatedData;
    },
    onMutate: async updatedData => {
      // Cancel any queries
      await Promise.all([
        queryClient.cancelQueries({queryKey: dataQueryKey}),
        queryClient.cancelQueries({
          queryKey: getQueryKey.detail(table, Number(updatedData[idColumn]), columns, enabled),
        }),
        statsView ? queryClient.cancelQueries({queryKey: getQueryKey.stats(statsView, enabled)}) : null,
      ]);

      // Get the previous data current page and the specific item
      const previousItemCache = queryClient.getQueryData<Database['public']['Tables'][T]['Row']>([
        table,
        updatedData[idColumn],
      ]);
      const {data: previousDataCache} = queryClient.getQueryData<{
        data: Database['public']['Tables'][T]['Row'][];
        total: number;
      }>(dataQueryKey) ?? {data: [], total: 0};

      // Optimistically update the detail page
      queryClient.setQueryData([table, updatedData[idColumn]], updatedData);

      // *NOTE*
      // We do not update the list view here. There are just too many variables at play.
      // Like sort order, filter, pagination, etc. We'll allow the list automatically update
      // like normal after the mutation has run.

      // Return rollback data
      return {
        previousData: previousDataCache,
        previousItem: previousItemCache,
      };
    },
    onError: (error, updatedData, context) => {
      // Rollback the optimistic updates on error
      if (context?.previousData)
        queryClient.setQueryData(dataQueryKey, {
          data: context.previousData,
          total: context.previousData.length,
        });
      if (context?.previousItem) queryClient.setQueryData([table, updatedData[idColumn]], context.previousItem);
      console.error(`Error updating ${table}:`, error);
      toast.error(`Error updating ${table}`);
    },
    onSuccess: (data: Database['public']['Tables'][T]['Row']) => {
      toast.success(`${table} updated successfully. Click here to view.`, {
        onClick: () => navigate(`/${table.toLowerCase()}s/${data[idColumn]}`),
      });

      // Invalidate all cached queries as updating a record could affect them all
      clearDataCache();
    },
  });

  /*********************************************************************
   * Deletes an existing record from the database.
   * @param {number} id - The ID of the item to be deleted.
   * @returns {Promise<Database['public']['Tables'][T]['Row']>}
   ********************************************************************/
  const {mutateAsync: deleteData} = useMutation({
    mutationKey: [table, 'delete'],
    mutationFn: async (id: number) => {
      // Sanity check to ensure that the defaultDeleteDataRef is set
      if (!defaultDeleteDataRef.current) defaultDeleteDataRef.current = await defaultDeleteData();

      const {data, error} = await SupabaseInstance.supabase
        .from(table)
        .update({...defaultDeleteDataRef.current} as never)
        .eq(idColumn.toString(), id)
        .select()
        .single<Database['public']['Tables'][T]['Row']>();

      // Reset the defaultDeleteData ref
      defaultDeleteDataRef.current = null;

      if (error) throw error;
      return data;
    },
    onMutate: async id => {
      // Cancel any queries
      await Promise.all([
        queryClient.cancelQueries({queryKey: dataQueryKey}),
        queryClient.cancelQueries({queryKey: getQueryKey.detail(table, id, columns, enabled)}),
        statsView ? queryClient.cancelQueries({queryKey: getQueryKey.stats(statsView, enabled)}) : null,
      ]);

      // Get the previous data current page and the specific item
      const previousStatsCache = statsView
        ? queryClient.getQueryData<Database['public']['Views'][S]['Row']>(getQueryKey.stats(statsView, enabled))
        : undefined;
      const previousItemCache = queryClient.getQueryData<Database['public']['Tables'][T]['Row']>(
        getQueryKey.detail(table, id, columns, enabled)
      );
      const {data: previousDataCache} = queryClient.getQueryData<{
        data: Database['public']['Tables'][T]['Row'][];
        total: number;
      }>(dataQueryKey) ?? {data: [], total: 0};

      // Get the previous item
      const previousItem: Database['public']['Tables'][T]['Row'] | undefined = previousDataCache.find(
        r => r[idColumn] === id
      );

      // Create a new item with the default delete data
      defaultDeleteDataRef.current = await defaultDeleteData();

      const newItem: Database['public']['Tables'][T]['Row'] = previousItem
        ? {...previousItem, ...defaultDeleteDataRef.current}
        : ({[idColumn]: id, ...defaultDeleteDataRef.current} as Database['public']['Tables'][T]['Row']);

      // Optimistically update default stats if they exist
      if (statsView)
        queryClient.setQueryData(
          getQueryKey.stats(statsView, enabled),
          (old: Database['public']['Views'][S]['Row'] | undefined) => {
            return old
              ? {
                  ...old,
                  active: 'active' in old && old.active !== null ? old.active - 1 : null,
                  inactive: 'inactive' in old && old.inactive !== null ? old.inactive + 1 : null,
                }
              : {};
          }
        );

      // Optimistically update the detail page
      queryClient.setQueryData([table, newItem[idColumn]], newItem);

      // Perform optimistic update the data list
      // This is just updating the row item, since we are not removing from the results this is fine. But if we want to remove an item from teh list
      // we'll most likely need to just refetch the data
      queryClient.setQueryData(
        dataQueryKey,
        (old: {data: Database['public']['Tables'][T]['Row'][]; total: number} | undefined) => {
          return old
            ? {
                ...old,
                data: old.data.map(item => (item[idColumn] === id ? newItem : item)),
              }
            : {data: [], total: 0};
        }
      );

      // Return rollback data
      return {
        previousData: previousDataCache,
        previousStats: previousStatsCache,
        previousItem: previousItemCache,
      };
    },
    onError: (error, id, context) => {
      // Rollback the optimistic updates on error
      if (context?.previousData)
        queryClient.setQueryData(dataQueryKey, {
          data: context.previousData,
          total: context.previousData.length,
        });
      if (context?.previousStats && statsView)
        queryClient.setQueryData(getQueryKey.stats(statsView, enabled), context.previousStats);
      if (context?.previousItem)
        queryClient.setQueryData(getQueryKey.detail(table, id, columns, enabled), context.previousItem);
      defaultDeleteDataRef.current = null;
      console.error(`Error deleting ${table}:`, error);
      toast.error(`Error deleting ${table}`);
    },
    // The data is also passed to the onSuccess function, but it is not used.
    // onSuccess: (data:Database['public']['Tables'][T]['Row']) => {
    //   toast.success('Item deleted successfully');
    //   clearDataCache();
    // },
    onSuccess: () => {
      toast.success('Item deleted successfully');
      clearDataCache();
    },
  });

  /*******************************************************************
   * Initialization
   *******************************************************************/
  // Authorization and error guard
  useEffect(() => {
    // Error guard
    if (errorData) {
      enabledRef.current = false;
      clearDataCache();
      if (errorData) throw new Error(`Error fetching ${table}`);
    }
    // Stats Error guard
    // Log error to the console, don't throw, all stats have defaults
    if (errorStats) console.error(`Error getting ${table} stats`, errorStats);
  }, [errorData, errorStats, clearDataCache, table]);

  return (
    <BaseContext.Provider
      value={{
        stats,
        data,
        isLoading: isLoadingData || isLoadingStats,
        isLoadingData,
        isLoadingStats,
        getData,
        getStats,
        pagination,
        enabled: enabledRef.current,
        dataError: errorData,
        statsError: errorStats,
        initialize,
        sortData,
        searchData: handleDebouncedSearchData,
        setEnabled: enabled => (enabledRef.current = enabled),
        setFilters: filters => (filtersRef.current = filters),
        refreshData,
        refreshStats,
        createData,
        updateData,
        deleteData,
      }}
    >
      {children}
    </BaseContext.Provider>
  );
};

/*******************************************************************
 * useBaseContext Hook
 *******************************************************************/
export const useBaseContext = <
  T extends keyof Database['public']['Tables'],
  C extends ColumnSelectionT<T>[],
  V extends keyof Database['public']['Views'] | undefined = undefined,
>(
  filters?: FilterT<T, C>[]
) => {
  const context = useContext<BaseGenericContextT<T, V, C>>(BaseContext);
  if (context && Object.keys(context).length === 0 && Object.getPrototypeOf(context) === Object.prototype) {
    throw new Error('useBaseData must be used within a BaseProvider');
  }

  if (filters) context.setFilters(filters);
  return context;
};
